import { Inject, Injectable } from '@angular/core';
import { RUNTIME_CONFIG, RuntimeConfig } from '../../core/runtime-config';

export interface GoogleMapsLibraries {
  Map: typeof google.maps.Map;
  LatLngBounds: typeof google.maps.LatLngBounds;
  AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement;
  InfoWindow: typeof google.maps.InfoWindow;
  Geocoder: typeof google.maps.Geocoder;
  AutocompleteSessionToken: typeof google.maps.places.AutocompleteSessionToken;
  AutocompleteSuggestion: typeof google.maps.places.AutocompleteSuggestion;
}

const GOOGLE_MAPS_LEGACY_CALLBACK_NAMES = [
  '__realtyGoogleMapsInit',
  '__clickhomGoogleMapsInit',
] as const;

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private loadingPromise: Promise<GoogleMapsLibraries | null> | null = null;

  constructor(@Inject(RUNTIME_CONFIG) private readonly runtimeConfig: RuntimeConfig) {}

  load(): Promise<GoogleMapsLibraries | null> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    if (typeof window === 'undefined' || !this.runtimeConfig.googleMapsApiKey) {
      return Promise.resolve(null);
    }

    this.loadingPromise = this.ensureScriptLoaded()
      .then(() => this.resolveLibraries())
      .catch((error) => {
        console.error('Failed to load Google Maps', error);
        return null;
      });

    return this.loadingPromise;
  }

  getMapId(): string {
    return this.runtimeConfig.googleMapsMapId || 'DEMO_MAP_ID';
  }

  private ensureScriptLoaded(): Promise<void> {
    if (this.hasGoogleMapsApi()) {
      return Promise.resolve();
    }

    const cleanupLegacyCallbacks = this.registerLegacyCallbacks();
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-maps-loader="true"]');
    if (existing) {
      return this.waitForGoogleMapsApi().finally(cleanupLegacyCallbacks);
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.dataset['googleMapsLoader'] = 'true';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
        this.runtimeConfig.googleMapsApiKey
      )}&v=weekly`;

      script.addEventListener(
        'load',
        () => {
          void this.waitForGoogleMapsApi()
            .then(() => {
              cleanupLegacyCallbacks();
              resolve();
            })
            .catch((error) => {
              cleanupLegacyCallbacks();
              reject(error);
            });
        },
        { once: true }
      );

      script.addEventListener(
        'error',
        () => {
          cleanupLegacyCallbacks();
          reject(new Error('google maps failed to load'));
        },
        { once: true }
      );

      document.head.appendChild(script);
    });
  }

  private hasGoogleMapsApi(): boolean {
    return typeof window.google?.maps?.importLibrary === 'function';
  }

  private registerLegacyCallbacks(): () => void {
    const previousCallbacks = new Map<
      (typeof GOOGLE_MAPS_LEGACY_CALLBACK_NAMES)[number],
      Window[(typeof GOOGLE_MAPS_LEGACY_CALLBACK_NAMES)[number]] | undefined
    >();

    GOOGLE_MAPS_LEGACY_CALLBACK_NAMES.forEach((callbackName) => {
      previousCallbacks.set(callbackName, window[callbackName]);
      window[callbackName] = () => undefined;
    });

    return () => {
      GOOGLE_MAPS_LEGACY_CALLBACK_NAMES.forEach((callbackName) => {
        const previousCallback = previousCallbacks.get(callbackName);
        if (previousCallback) {
          window[callbackName] = previousCallback;
          return;
        }

        delete window[callbackName];
      });
    };
  }

  private async waitForGoogleMapsApi(): Promise<void> {
    const timeoutAt = Date.now() + 4000;

    while (Date.now() < timeoutAt) {
      if (this.hasGoogleMapsApi()) {
        return;
      }

      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }

    throw new Error('google maps api missing');
  }

  private async resolveLibraries(): Promise<GoogleMapsLibraries> {
    const [mapsLibrary, coreLibrary, markerLibrary, geocodingLibrary, placesLibrary] = await Promise.all([
      google.maps.importLibrary('maps') as Promise<google.maps.MapsLibrary>,
      google.maps.importLibrary('core') as Promise<google.maps.CoreLibrary>,
      google.maps.importLibrary('marker') as Promise<google.maps.MarkerLibrary>,
      google.maps.importLibrary('geocoding') as Promise<google.maps.GeocodingLibrary>,
      google.maps.importLibrary('places') as Promise<google.maps.PlacesLibrary>,
    ]);

    return {
      Map: mapsLibrary.Map,
      LatLngBounds: coreLibrary.LatLngBounds,
      AdvancedMarkerElement: markerLibrary.AdvancedMarkerElement,
      InfoWindow: mapsLibrary.InfoWindow,
      Geocoder: geocodingLibrary.Geocoder,
      AutocompleteSessionToken: placesLibrary.AutocompleteSessionToken,
      AutocompleteSuggestion: placesLibrary.AutocompleteSuggestion,
    };
  }
}
